import {Color, Icon} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import * as NavConstants from 'constants/NavConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';

import {makeNewDraftState} from '../ChatDraftState';
import {openDraftChat} from '../sidepanel/ChatRouterUtil';
import StartChatInput from './StartChatInput';

export const ENTITY_ASK_ALLOWED_TABS = new Set([
  'metadata',
  'relevant-items-tab',
  'entity-results',
  'storage',
]);

interface EntityAskButtonProps {
  fileId: string;
  variant: string;
}

export default function EntityAskButton({fileId, variant}: EntityAskButtonProps): JSX.Element {
  const hasTrackedImpression = useRef(false);

  const modelType = APIUtil.apiIdToType(fileId);

  useEffect(() => {
    hasTrackedImpression.current = false;
  }, [fileId]);

  useEffect(() => {
    if (!hasTrackedImpression.current) {
      hasTrackedImpression.current = true;
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_ENTITY_ASK_BUTTON, {
        itemId: fileId,
        modelType,
        variant,
      });
    }
  }, [fileId, modelType, variant]);

  if (variant === 'inline') {
    return (
      <StartChatInput
        itemId={fileId}
        agentType={AgentType.ASK}
        buttonSize={ButtonSize.MINI}
        className="u-mini-spacing-right"
        extraAnalyticsProperties={{variant}}
      />
    );
  }

  // "chat" variant — button that opens sidepanel directly
  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      className={classNames(css(styles.askButton), 'u-mini-spacing-right')}
      size={ButtonSize.MINI}
      icon={<Icon icon="sparkle" className={css(styles.sparkleIcon)} />}
      text="Ask"
      aria-label={`Ask about this ${typeToName(modelType)}`}
      onClick={() => {
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_ENTITY_ASK_BUTTON, {
          itemId: fileId,
          modelType,
          variant,
        });
        openDraftChat(makeNewDraftState(AgentType.ASK), {});
        SidepanelActions.openSection(NavConstants.SECTIONS.AI);
      }}
      dataTestAttrs={{component: 'EntityAskButton', element: 'button'}}
    />
  );
}

const styles = StyleSheet.create({
  askButton: {
    backgroundColor: Color.BLUE_100,
    border: 'none',
    color: Color.BRAND_ROYAL,
    ':hover': {
      backgroundColor: Color.BLUE_150,
    },
  },
  sparkleIcon: {
    color: Color.BRAND_ROYAL,
    flexShrink: 0,
    height: 14,
    width: 14,
  },
});
